/**
 * MessageViewModal - Модальное окно просмотра сообщения
 */
import React from 'react';
import {
  Modal,
  Text,
  Group,
  Stack,
  Badge,
  Divider,
  Box,
  Button,
} from '@mantine/core';
import {
  IconPencil,
  IconBrandWhatsapp,
  IconBrandTelegram,
  IconBrandVk,
  IconPhone,
  IconUser,
  IconCalendar,
  IconEdit,
  IconMessage,
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';

/**
 * Конфигурация мессенджеров
 */
const MESSENGER_CONFIG = {
  whatsapp: { icon: IconBrandWhatsapp, color: 'green', label: 'WhatsApp', bgColor: '#dcfce7', iconColor: '#16a34a' },
  telegram: { icon: IconBrandTelegram, color: 'blue', label: 'Telegram', bgColor: '#dbeafe', iconColor: '#2563eb' },
  viber: { icon: IconPhone, color: 'grape', label: 'Viber', bgColor: '#f3e8ff', iconColor: '#9333ea' },
  vk: { icon: IconBrandVk, color: 'indigo', label: 'VK', bgColor: '#e0e7ff', iconColor: '#4f46e5' },
};

/**
 * Форматирование даты
 */
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

/**
 * Форматирование контакта
 */
const formatContact = (contact, messengerType) => {
  if (!contact) return '';

  if (['whatsapp', 'viber'].includes(messengerType)) {
    const digits = contact.replace(/\D/g, '');
    if (digits.length === 11 && (digits.startsWith('7') || digits.startsWith('8'))) {
      return `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
    }
    if (digits.length === 10) {
      return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
    }
  }

  return contact;
};

/**
 * Компонент для отображения поля
 */
const InfoField = ({ icon: Icon, label, value, children }) => {
  if (!value && !children) return null;

  return (
    <Group gap="sm" align="flex-start">
      <Box
        style={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          backgroundColor: tokens.colors.neutral[2],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={16} color={tokens.colors.neutral[7]} />
      </Box>
      <Box style={{ flex: 1 }}>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        {children || <Text size="sm">{value}</Text>}
      </Box>
    </Group>
  );
};

const MessageViewModal = ({
  opened,
  onClose,
  message,
  onEdit,
}) => {
  if (!message) return null;

  const messengerConfig = MESSENGER_CONFIG[message.messenger_type];
  const MessengerIcon = messengerConfig?.icon || IconMessage;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Сообщение"
      size="md"
      radius="lg"
    >
      <Stack gap="lg">
        {/* Заголовок с мессенджером и типом */}
        <Group>
          <Box
            style={{
              width: 56,
              height: 56,
              borderRadius: '12px',
              backgroundColor: messengerConfig?.bgColor || tokens.colors.neutral[2],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MessengerIcon size={28} color={messengerConfig?.iconColor || tokens.colors.neutral[7]} />
          </Box>
          <Box style={{ flex: 1 }}>
            <Group gap="xs" wrap="wrap">
              <Badge
                variant="light"
                color={messengerConfig?.color || 'gray'}
                size="lg"
                radius="xl"
              >
                {messengerConfig?.label || message.messenger_type}
              </Badge>
              <Badge
                variant="filled"
                color={message.message_type === 'primary' ? 'green' : 'yellow'}
                size="lg"
                radius="xl"
              >
                {message.message_type === 'primary' ? 'Первичное' : 'Вторичное'}
              </Badge>
              {message.is_edited && (
                <Badge
                  variant="filled"
                  color="orange"
                  size="lg"
                  radius="xl"
                  leftSection={<IconEdit size={12} />}
                >
                  Изменено{message.edit_count > 1 ? ` (${message.edit_count})` : ''}
                </Badge>
              )}
            </Group>
            <Text size="sm" fw={500} mt={4}>
              {formatContact(message.messenger_contact, message.messenger_type)}
            </Text>
          </Box>
        </Group>

        <Divider />

        {/* Информация */}
        <Stack gap="md">
          <InfoField icon={IconUser} label="Отправитель">
            <Text size="sm">
              {message.sender?.full_name || message.sender?.email || 'Не указан'}
            </Text>
          </InfoField>

          <InfoField
            icon={IconCalendar}
            label="Дата и время отправки"
            value={formatDate(message.sent_datetime)}
          />

          {message.created_by && message.created_by.id !== message.sender?.id && (
            <InfoField icon={IconUser} label="Кто записал">
              <Text size="sm">
                {message.created_by.full_name || message.created_by.email}
              </Text>
            </InfoField>
          )}
        </Stack>

        <Divider />

        {/* Текст сообщения */}
        <Stack gap="sm">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Текст сообщения
          </Text>
          <Box
            p="md"
            style={{
              backgroundColor: tokens.colors.neutral[1],
              borderRadius: '8px',
            }}
          >
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {message.message_text}
            </Text>
          </Box>
        </Stack>

        <Divider />

        {/* Кнопки */}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>
            Закрыть
          </Button>
          <Button
            leftSection={<IconPencil size={16} />}
            onClick={() => onEdit(message)}
          >
            Редактировать
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default MessageViewModal;
